<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue'
import 'vue-tel-input/vue-tel-input.css'
import { PhoneNumberUtil } from 'google-libphonenumber'
const phoneUtil = PhoneNumberUtil.getInstance()

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | boolean | Date
    error?: boolean
    errorMessage?: string
    label?: string
    required?: boolean
    events?: boolean
    id?: string
    highlightColor?: string
    highlightValidation?: boolean
  }>(),
  {
    required: false,
    label: '',
    modelValue: '',
    error: false,
    errorMessage: '',
    id: '',
    highlightValidation: false,
    highlightColor: '',
    events: false,
  }
)

const emit = defineEmits([
  'update:modelValue',
  'input',
  'country-code',
  'validate',
  'blur',
])
defineOptions({ inheritAttrs: false })
const attrs = useAttrs()

type PhoneNumber = {
  country: { dialCode: string }
  countryCallingCode: string
  countryCode: string
  number: string
  valid: boolean
  formatted: string
  nationalNumber: string
}

const isPhoneValid = ref(true)

/** Valor INTERNO do campo (o que o usuário vê/digita). */
const inputValue = ref<string>(String(props.modelValue ?? ''))

/** erro efetivo: externo (form) OU interno (validação local) */
const hasError = computed(() => Boolean(props.error || !isPhoneValid.value))

/** mensagem padrão quando nada veio de fora */
const fallbackMsg = computed(() => props.errorMessage || 'validation.phone')

function handleInput(_number: string, phoneObject: PhoneNumber) {
  // Mantém a experiência do usuário: não force inputValue aqui.
  // Apenas emite o que vai para o FORM (valor normalizado).
  const national = (phoneObject?.nationalNumber || '').replace(/\D+/g, '')

  const dial = phoneObject?.countryCallingCode
    ? `+${phoneObject.countryCallingCode}`
    : ''
  const trimmed = (_number || '').replace(/\s+/g, '')
  const onlyDialCode = !!dial && trimmed === dial

  // Se só tem DDI, o FORM recebe vazio (campo "não preenchido")
  if (!national || onlyDialCode) {
    emit('update:modelValue', '')
    emit('input', '')
    emit('country-code', phoneObject.countryCode)
    isPhoneValid.value = true
    emit('validate', true)
    return
  }

  // FORM recebe apenas o nacional
  emit('update:modelValue', national)
  emit('input', national)
  emit('country-code', phoneObject.countryCode)

  // Validação local (use nacional com região detectada)
  try {
    isPhoneValid.value = phoneUtil.isValidNumberForRegion(
      phoneUtil.parse(national, phoneObject.countryCode),
      phoneObject.countryCode
    )
  } catch {
    isPhoneValid.value = false
  }
  emit('validate', isPhoneValid.value)
}

function onBlur() {
  // Opcional: ao sair do campo, se só tiver DDI, garante FORM vazio
  const raw = String(inputValue.value || '').replace(/\s+/g, '')
  if (/^\+\d{1,3}$/.test(raw)) {
    emit('update:modelValue', '')
    emit('input', '')
    emit('validate', true)
  }
  emit('blur')
}
</script>

<template>
  <p
    v-if="label"
    :class="[
      'styleText',
      'text-body-2',
      'font-weight-medium',
      {
        'text-error': hasError,
        'mb-2 mt-2': !events,
        'text-grey-darken-1 ms-2': events,
      },
    ]"
  >
    {{ label }} {{ required ? '*' : '' }}
  </p>

  <vue-tel-input
    v-bind="{ ...attrs }"
    :id="id"
    v-model="inputValue"
    :input-options="{ placeholder: '', showDialCode: true }"
    :auto-format="true"
    default-country="BR"
    mode="international"
    :class="[
      'custom-phone-input',
      {
        'border-error': hasError,
        'border-success': highlightValidation && !hasError && inputValue,
      },
    ]"
    @on-input="handleInput"
    @blur="onBlur"
  />

  <div
    v-if="hasError"
    id="error-message"
    class="text-caption my-1 ml-4 text-error"
    style="height: 1rem"
  >
    {{ $t(fallbackMsg) }}
  </div>
</template>

<style>
.vue-tel-input {
  position: relative; /* para posicionar o dropdown */
  border: 1px solid #ababab !important;
  border-radius: 4px !important;
  background-color: #fff !important;
}

.vue-tel-input:hover {
  box-shadow: none !important;
  border-color: var(--v-theme-on-surface) !important;
}

.vue-tel-input:focus-within {
  border-color: var(--v-theme-primary) !important;
  box-shadow: none !important;
  border-width: 2px !important;
}

.vue-tel-input input {
  border-left: 1px solid #ababab !important;
  border-radius: 0 4px 4px 0 !important;
}

.vti__input {
  background-color: #fff;
  height: 46px;
}

/* dropdown ocupa toda a largura e mostra até 10 itens */
.vue-tel-input .vti__dropdown {
  position: static !important;
}

.vue-tel-input .vti__dropdown-list {
  position: absolute !important;
  left: 0 !important;
  right: 0 !important;
  width: auto !important;
  min-width: 100% !important;
  max-width: none !important;

  max-height: calc(36px * 10) !important; /* até 10 itens */
  overflow-y: auto !important;

  z-index: 2100; /* acima de dialogs */
  box-sizing: border-box;
  background-color: #fff !important;
  border: 1px solid #ccc;
  border-radius: 4px;
}

/* estiliza cada item da lista */
.vue-tel-input .vti__dropdown-item {
  height: 36px;
  line-height: 36px;
  padding: 0 12px;
  cursor: pointer;
}

.vue-tel-input .vti__dropdown-item:hover {
  background-color: var(--v-theme-surface-variant, #f5f5f5);
}

/* estiliza o dropdown aberto */
.vti__dropdown:hover {
  background-color: transparent !important;
}

/* texto do país em peso normal */
.vti__dropdown-item strong {
  font-weight: 400 !important;
}

/* estado de erro */
.vue-tel-input.custom-phone-input {
  border: 1px solid #ababab;
  border-radius: 4px !important;
  background-color: #fff !important;
}

.vue-tel-input.custom-phone-input.border-error {
  border: 1px solid #f1002e !important;
}

/* responsivo (mantém consistência no mobile) */
@media (max-width: 600px) {
  .vue-tel-input .vti__dropdown-list {
    max-height: calc(36px * 10) !important; /* mantém 10 itens */
    overflow-y: auto !important;
  }
}
</style>
